import { createStyles, Grid, makeStyles, Typography } from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import TextField from '../TextField/TextField'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(10),
      paddingBottom: theme.spacing(10),
      [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        fontSize: '0.5rem',
      },
    },
    container: {
      background: theme.palette.common.white,
      maxWidth: 600,
      margin: 'auto',
      padding: `0 ${theme.spacing(4)}px`,
    },
    inputsHeader: {
      padding: `${theme.spacing(0.25)}px ${theme.spacing(0)}px`,
    },
    alignCenter: {
      textAlign: 'center',
    },
    braSizeContainer: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      fontSize: '1.5rem',
      background: theme.palette.primary.light,
      borderRadius: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        margin: 0,
      },
    },
    braSizeInfo: {
      fontSize: '1rem',
    },
    submitButton: {
      backgroundColor: theme.palette.primary.light,
    },
    error: {
      color: theme.palette.error.main + ' !important',
      fontWeight: 700,
    },
  })
)

type BraSizeGuideFormKeys =
  | 'not_tight'
  | 'slightly_tight'
  | 'very_tight'
  | 'standing_up_straight'
  | 'leaning_forward'
  | 'lying_on_your_back'

type BraSizeGuideFormProps = {
  [key in BraSizeGuideFormKeys]: string
}

const tableDos = [
  { min: 67, max: 67, taille: '70/75' },
  { min: 68, max: 71, taille: '75' },
  { min: 72, max: 72, taille: '75/80' },
  { min: 73, max: 76, taille: '80' },
  { min: 77, max: 77, taille: '80/85' },
  { min: 78, max: 81, taille: '85' },
  { min: 82, max: 82, taille: '85/90' },
  { min: 83, max: 86, taille: '90' },
  { min: 87, max: 87, taille: '90/95' },
  { min: 88, max: 91, taille: '95' },
  { min: 92, max: 92, taille: '95/100' },
  { min: 93, max: 96, taille: '100' },
  { min: 97, max: 97, taille: '100/105' },
  { min: 98, max: 101, taille: '105' },
  { min: 102, max: 102, taille: '105/110' },
  { min: 103, max: 106, taille: '110' },
  { min: 107, max: 107, taille: '110/115' },
  { min: 108, max: 111, taille: '115' },
  { min: 112, max: 112, taille: '115/120' },
  { min: 113, max: 116, taille: '120' },
  { min: 117, max: 117, taille: '120/125' },
  { min: 118, max: 121, taille: '125' },
  { min: 122, max: 122, taille: '125/130' },
  { min: 123, max: 126, taille: '130' },
  { min: 127, max: 127, taille: '130/135' },
  { min: 128, max: 131, taille: '135' },
  { min: 132, max: 132, taille: '135/140' },
  { min: 133, max: 136, taille: '140' },
  { min: 137, max: 141, taille: '140/145' },
  { min: 142, max: 145, taille: '145' },
]

const tableBonnets = [
  { min: 1, max: 3, bonnet: 'AA/A' },
  { min: 4, max: 5, bonnet: 'A' },
  { min: 6, max: 6, bonnet: 'B/A' },
  { min: 7, max: 7, bonnet: 'B' },
  { min: 8, max: 8, bonnet: 'C/B' },
  { min: 9, max: 10, bonnet: 'C' },
  { min: 11, max: 11, bonnet: 'D/C' },
  { min: 12, max: 12, bonnet: 'D' },
  { min: 13, max: 13, bonnet: 'E/D' },
  { min: 14, max: 15, bonnet: 'E' },
  { min: 16, max: 16, bonnet: 'F/E' },
  { min: 17, max: 17, bonnet: 'F' },
  { min: 18, max: 18, bonnet: 'G/F' },
  { min: 19, max: 20, bonnet: 'G' },
  { min: 21, max: 21, bonnet: 'H/G' },
  { min: 22, max: 22, bonnet: 'H' },
  { min: 23, max: 23, bonnet: 'I/H' },
  { min: 24, max: 25, bonnet: 'I' },
  { min: 26, max: 26, bonnet: 'J/I' },
  { min: 27, max: 27, bonnet: 'J' },
  { min: 28, max: 28, bonnet: 'K/J' },
  { min: 29, max: 30, bonnet: 'K' },
  { min: 31, max: 31, bonnet: 'L/K' },
  { min: 32, max: 32, bonnet: 'L' },
]
const BraSizeGuide = () => {
  const classes = useStyles()
  const [form, setForm] = useState<BraSizeGuideFormProps>({
    not_tight: '',
    slightly_tight: '',
    very_tight: '',
    standing_up_straight: '',
    leaning_forward: '',
    lying_on_your_back: '',
  })
  const [braSize, setBraSize] = useState<string | undefined>()
  const [calculationError, setCalculationError] = useState<string | undefined>()

  const handleChange = (key: BraSizeGuideFormKeys, value: string) => {
    setForm({
      ...form,
      [key]: value,
    })
  }

  const handleSubmit = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const underbust = [
      +form.not_tight,
      +form.slightly_tight,
      +form.very_tight,
    ].filter((val) => !isNaN(val) && val > 0)
    const bust = [
      +form.standing_up_straight,
      +form.leaning_forward,
      +form.lying_on_your_back,
    ].filter((val) => !isNaN(val) && val > 0)
    if (underbust.length !== 3 || bust.length !== 3) {
      return
    }
    event.preventDefault()

    const avgUnderbust = Math.round(underbust.reduce((a, b) => a + b, 0) / 3)
    const avgBust = Math.round(bust.reduce((a, b) => a + b, 0) / 3)
    const diff = avgBust - avgUnderbust
    const tailleDos = tableDos.find(
      (r) => avgUnderbust >= r.min && avgUnderbust <= r.max
    )
    const tailleBonnet = tableBonnets.find(
      (r) => diff >= r.min && diff <= r.max
    )
    if (!tailleDos || !tailleBonnet) {
      setCalculationError('error_size_not_found')
      setBraSize(undefined)
      return
    }
    const tailles = tailleDos.taille.split('/')
    const bonnets = tailleBonnet.bonnet.split('/')
    const taillesFinales = `${tailles[0]}${bonnets[0]}`
    setCalculationError(undefined)
    setBraSize(taillesFinales)
  }

  const { t } = useTranslation()

  return (
    <section className={`contact-page ${classes.root}`}>
      <form className={classes.container}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant={'h1'} align="center">
              {t('texts:size_guide_page:size_guide')}
            </Typography>
          </Grid>
          <Grid className={classes.inputsHeader}>
            <Typography variant={'h3'}>
              {t('texts:size_guide_page:measure_your_back_circumference')}
            </Typography>
          </Grid>
          <Grid xs={12}>
            <TextField
              label={t('texts:size_guide_page:not_tight')}
              type="number"
              name="email"
              placeholder="ex: 72cm"
              value={form.not_tight}
              required={true}
              onChange={handleChange.bind(null, 'not_tight')}
              noLabelPaddingLeft
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              label={t('texts:size_guide_page:slightly_tight')}
              type="number"
              name="email"
              placeholder="ex: 70cm"
              value={form.slightly_tight}
              required={true}
              onChange={handleChange.bind(null, 'slightly_tight')}
              noLabelPaddingLeft
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              label={t('texts:size_guide_page:very_tight')}
              type="number"
              name="email"
              placeholder="ex: 68cm"
              value={form.very_tight}
              required={true}
              onChange={handleChange.bind(null, 'very_tight')}
              noLabelPaddingLeft
            />
          </Grid>
          <Grid xs={12} className={classes.inputsHeader}>
            <Typography variant={'h3'}>
              {t('texts:size_guide_page:measure_your_bust')}
            </Typography>
          </Grid>
          <Grid xs={12}>
            <TextField
              label={t('texts:size_guide_page:standing_up_straight')}
              type="number"
              name="email"
              placeholder="ex: 85cm"
              value={form.standing_up_straight}
              required={true}
              onChange={handleChange.bind(null, 'standing_up_straight')}
              noLabelPaddingLeft
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              label={t('texts:size_guide_page:leaning_forward')}
              type="number"
              name="email"
              placeholder="ex: 87cm"
              value={form.leaning_forward}
              required={true}
              onChange={handleChange.bind(null, 'leaning_forward')}
              noLabelPaddingLeft
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              label={t('texts:size_guide_page:lying_on_your_back')}
              type="number"
              name="email"
              placeholder="ex: 84cm"
              value={form.lying_on_your_back}
              required={true}
              onChange={handleChange.bind(null, 'lying_on_your_back')}
              noLabelPaddingLeft
            />
          </Grid>
          <Grid item xs={12} className={classes.alignCenter}>
            <Button
              type="submit"
              variant={'contained'}
              color={'secondary'}
              onClick={handleSubmit}
              className={classes.submitButton}
            >
              {t('texts:size_guide_page:calculate_size')}
            </Button>
          </Grid>
          {calculationError && (
            <Grid item xs={12} className={classes.alignCenter}>
              <Typography variant={'body1'} className={classes.error}>
                {t(`texts:size_guide_page:${calculationError}`)}
              </Typography>
            </Grid>
          )}
          {braSize && (
            <Grid item xs={12} className={classes.braSizeContainer}>
              <Typography
                variant={'h3'}
                align="center"
                className={classes.braSizeInfo}
              >
                ✨ {t('texts:size_guide_page:bra_size')}: {braSize} ✨
              </Typography>
            </Grid>
          )}
        </Grid>
      </form>
    </section>
  )
}

export default BraSizeGuide
